import { type ReactNode, useEffect, useRef } from 'react'
import { Header, LedgerLink, locPath, serif } from './ui'

export const th = 'py-2.5 pr-4 text-left text-[12px] font-medium text-[var(--color-muted)]'
export const input =
  'w-full border-0 border-b border-[var(--color-border)] bg-transparent pb-1 pt-0.5 text-[14px] text-[var(--color-fg)] placeholder:italic placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'

/** SSR-safe, fixed-zone timestamp. */
export function utc(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children and sits inside this label
    <label className="block">
      <span className="mb-1 flex items-baseline gap-2 text-[12px] text-[var(--color-muted)]">
        {label}
        {hint ? <span className="italic opacity-80">{hint}</span> : null}
      </span>
      {children}
    </label>
  )
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  label,
}: {
  value: string
  onChange: (v: string) => void
  options: [string, string][]
  placeholder?: string
  label?: string
}) {
  return (
    <select aria-label={label} className={input} value={value} onChange={(e) => onChange(e.target.value)}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  )
}

type Variant = 'primary' | 'quiet' | 'danger'

export function Btn({
  children,
  onClick,
  type = 'button',
  variant = 'quiet',
  disabled,
  title,
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  variant?: Variant
  disabled?: boolean
  title?: string
}) {
  const v =
    variant === 'primary'
      ? 'bg-[var(--color-accent)] px-4 py-1.5 font-semibold text-[var(--color-accent-fg)] hover:opacity-90'
      : variant === 'danger'
        ? 'px-1 py-1.5 text-[var(--color-accent)] underline decoration-[var(--color-border)] underline-offset-4 hover:decoration-[var(--color-accent)]'
        : 'px-1 py-1.5 text-[var(--color-fg)] underline decoration-[var(--color-border)] underline-offset-4 hover:decoration-[var(--color-fg)]'
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`cursor-pointer text-[13.5px] transition-opacity focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40 ${v}`}
    >
      {children}
    </button>
  )
}

/** Inline result line in italic serif; errors are role=alert. */
export function Inline({ tone, children }: { tone: 'ok' | 'bad' | 'muted'; children: ReactNode }) {
  const color = tone === 'ok' ? 'var(--v3-ok)' : tone === 'bad' ? 'var(--color-accent)' : 'var(--color-muted)'
  return (
    <p role={tone === 'bad' ? 'alert' : 'status'} className="text-[14px] italic" style={{ ...serif, color }}>
      {children}
    </p>
  )
}

/** A typeset tag: small caps text between rules rather than a pill. */
export function Tag({ children, tone }: { children: ReactNode; tone?: 'ok' | 'bad' | 'warn' | 'off' }) {
  const color =
    tone === 'ok' ? 'var(--v3-ok)' : tone === 'bad' || tone === 'warn' ? 'var(--color-accent)' : 'var(--v3-off)'
  return (
    <span
      className="inline-block border-y px-1 text-[10.5px] font-semibold uppercase tracking-[0.12em]"
      style={{ color, borderColor: color }}
    >
      {children}
    </span>
  )
}

/** Modal sheet. Escape/backdrop closes; focus moves in; Cmd/Ctrl+Enter triggers `onSubmit`. */
export function Dialog({
  open,
  title,
  eyebrow,
  onClose,
  onSubmit,
  children,
  footer,
}: {
  open: boolean
  title: string
  eyebrow?: string
  onClose: () => void
  onSubmit?: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSubmit?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, onSubmit])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgb(20 14 8 / 0.5)' }}>
      <button type="button" aria-label="Close dialog" className="absolute inset-0 cursor-default" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="relative max-h-[88vh] w-full max-w-[480px] overflow-auto border-t-4 border-[var(--v3-rule-strong)] bg-[var(--color-surface)] px-7 pb-5 pt-5 shadow-[0_18px_50px_rgb(20_14_8/0.3)] outline-none"
      >
        {eyebrow ? (
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted)]">
            {eyebrow}
          </p>
        ) : null}
        <h2 className="text-[24px] font-semibold leading-tight tracking-[-0.01em]" style={serif}>
          {title}
        </h2>
        <div className="mt-4 grid gap-4">{children}</div>
        {footer ? (
          <div className="mt-6 flex items-center justify-end gap-5 border-t border-[var(--color-border)] pt-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  danger?: boolean
  pending?: boolean
  error?: string | null
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Dialog
      open={open}
      eyebrow={danger ? 'Please confirm' : 'Confirm'}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={pending}>
            Cancel
          </Btn>
          <Btn variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={pending}>
            {pending ? 'Working…' : confirmLabel}
          </Btn>
        </>
      }
    >
      <div className="text-[16px] leading-[1.55] text-[var(--color-muted)]" style={serif}>
        {body}
      </div>
      {error ? <Inline tone="bad">{error}</Inline> : null}
    </Dialog>
  )
}

/** Page header with the shared ledger breadcrumb. */
export function PageHeader({
  idOrCode,
  section,
  page,
  title,
  standfirst,
  aside,
}: {
  idOrCode: string
  section: 'Network' | 'POS'
  page: string
  title: string
  standfirst?: ReactNode
  aside?: ReactNode
}) {
  return (
    <Header
      eyebrow={
        <>
          <LedgerLink to="/locations">Locations</LedgerLink> /{' '}
          <LedgerLink to={locPath(idOrCode)}>{idOrCode}</LedgerLink> / {section} / {page}
        </>
      }
      title={title}
      standfirst={standfirst}
      aside={aside}
    />
  )
}

export function ActionRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-4">{children}</div>
}
